/**
 * The two pieces of sign-in the CONSOLE SHELL touches: the control that ends
 * a session, and the rule that a new session gets a new console.
 *
 * ## Why these are not in `AuthGate.tsx`
 *
 * `AuthGate` imports `@clerk/clerk-react`. The shell must not: the console
 * has a real, supported UNGATED mode (no publishable key — see `AuthGate`'s
 * header), and Clerk's components throw outside a `<ClerkProvider>`. A shell
 * that imported `<UserButton>` directly would either crash that mode or need
 * a Clerk provider in every test that renders a sidebar.
 *
 * So the shell mounts a SLOT and the auth layer fills it. `AuthGate` puts
 * Clerk's `<UserButton>` in when Clerk is configured, and puts nothing in when
 * it is not — which is the honest answer there, because a console with no
 * session has nothing to sign out of and a control that did nothing would be
 * worse than no control.
 *
 * ## The bug that made all of this necessary
 *
 * `AuthIdentity` existed and was rendered NOWHERE. A signed-in console had no
 * way out, so a second account could not be reached at all — and the second
 * account is the one that belongs to two organizations, so half the
 * workspace behaviour was unreachable by a human.
 */

import { createContext, Fragment, useContext, type ReactNode } from "react";

/**
 * What the auth layer offers as "you, and how to leave".
 *
 * A `ReactNode` rather than a `{ signOut }` callback on purpose: Clerk's
 * `<UserButton>` is also how a user ADDS a second account and switches
 * between them without signing out, and reducing it to one function would
 * throw that away — along with the avatar, which is the thing people
 * actually recognize.
 *
 * `null` is the ungated console. Exported so tests can supply a stand-in
 * without a Clerk instance.
 */
export const AuthIdentityContext = createContext<ReactNode>(null);

/**
 * The identity control, wherever the shell puts it.
 *
 * Renders NOTHING when the auth layer offered nothing. That is not a
 * degraded state — see the file header.
 */
export function AuthIdentity() {
  const control = useContext(AuthIdentityContext);
  if (!control) return null;
  return <span data-testid="auth-identity">{control}</span>;
}

/**
 * Everything below this belongs to ONE signed-in identity.
 *
 * When `identity` changes, the whole subtree is thrown away and rebuilt. That
 * is the entire mechanism, and it is a `key`, not a cache eviction pass,
 * because the console's per-view state is not in one place to evict: every
 * view holds its own `useResource` state and the shell holds an open
 * `EventSource` on `/api/live`. A remount clears all of it at once and cannot
 * be partially forgotten the next time a view is added.
 *
 * ## Why signing out is not enough on its own
 *
 * Clerk supports MULTI-SESSION: "switch account" swaps the active session
 * WITHOUT unmounting `<SignedIn>`. The console would keep rendering the
 * previous account's rows, keep its `useResource` state, and keep a live
 * stream opened under the old credential, all while the identity strip named
 * someone else. Stale rows under a new name are worse than an error — the
 * user has no way to tell they are looking at the wrong tenant's work.
 *
 * `null` (signed out) is a distinct key from every user id, so signing out
 * tears the tree down too rather than leaving a rendered console behind the
 * sign-in screen.
 */
export function IdentityScope({
  identity,
  children,
}: {
  /** The signed-in Clerk user id, or `null` when there is no session. */
  identity: string | null;
  children: ReactNode;
}) {
  // A keyed FRAGMENT: React treats a changed key as a different element, so
  // the subtree unmounts and remounts. A fragment rather than a wrapper
  // element because this must add nothing to the DOM — the shell's own root
  // is a `height: 100vh` flex container and an interposed div is a layout
  // change smuggled in by an auth concern.
  return <Fragment key={identity ?? "signed-out"}>{children}</Fragment>;
}

// ---------------------------------------------------------------------------
// WHO you are, as opposed to how you leave
// ---------------------------------------------------------------------------

/**
 * The signed-in person's own strings, as the auth layer knows them.
 *
 * ## Why the client has to be the source
 *
 * The server answers `caller` on every route from `resolveCallerIdentity`,
 * which reads the Mongo `users` mirror. Those rows carry ONLY `userId`,
 * `clerkUserId` and integration tokens — no name, no email. `upsertUserFromClerk`
 * writes the name and email, but it runs only from the Clerk webhook, which
 * never populated them. Verified directly against the staging database.
 *
 * So the server was right and the footer was still wrong: it printed
 * `user_3E3fUozuA7b…` because that was genuinely all the mirror knew. The
 * browser, meanwhile, is signed in through Clerk and is holding the person's
 * full profile the whole time. `AuthGate` puts it here; the shell reads it
 * through `resolveIdentity` and keeps the server's `caller` as the fallback
 * for the UNGATED console, which has no Clerk to ask.
 *
 * `null` means "the auth layer knows nobody" — Clerk still loading, or no
 * Clerk at all. Deliberately distinct from `{}` ("somebody, with no name on
 * file"), because only the first may fall through to the mirror.
 */
export interface AuthPerson {
  name?: string;
  email?: string;
}

/** Filled by `AuthGate` from Clerk's `useUser()`. `null` in the ungated
 *  console. Exported so tests can supply a person without a Clerk instance —
 *  the shell must never import Clerk (see the file header). */
export const AuthPersonContext = createContext<AuthPerson | null>(null);

/** The signed-in person, or `null`. */
export function useAuthPerson(): AuthPerson | null {
  return useContext(AuthPersonContext);
}

/** Where the printed identity came from. The footer LABELS an `id` rather
 *  than presenting it in the slot where a name goes — an opaque id shown as
 *  somebody's name is what made a user believe they were signed into the
 *  wrong account. */
export type IdentitySource = "name" | "email" | "id" | "none";

export interface ResolvedIdentity {
  label: string;
  source: IdentitySource;
}

/** Present, once trimmed. `""` and `"   "` are not answers: taken as a name
 *  they produce an EMPTY bold line on a signed-in console, which reads as
 *  "signed out". */
function present(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * The PERSON, from whichever source can name them.
 *
 * Clerk first because it is the live profile and the mirror is a copy taken
 * at webhook time — a renamed user should see their new name. Then the
 * mirror, which is all the ungated console has. Then the email from either.
 * Then, only then, the id, reported as an id.
 */
export function resolveIdentity(
  person: AuthPerson | null | undefined,
  caller: { userId: string; name?: string; email?: string } | undefined,
): ResolvedIdentity {
  const name = present(person?.name) ?? present(caller?.name);
  if (name) return { label: name, source: "name" };

  const email = present(person?.email) ?? present(caller?.email);
  if (email) return { label: email, source: "email" };

  const id = present(caller?.userId);
  if (id) return { label: id, source: "id" };

  return { label: "", source: "none" };
}

/**
 * The shape of Clerk's `useUser().user` that this console reads.
 *
 * Structural rather than Clerk's own `UserResource`: this module must not
 * import `@clerk/clerk-react` — the shell imports it, and the ungated console
 * has no Clerk at all (see the file header). `AuthGate` passes Clerk's real
 * user in and it type-checks by structure.
 */
export interface ClerkUserLike {
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  primaryEmailAddress?: { emailAddress?: string | null } | null;
  emailAddresses?: ReadonlyArray<{ emailAddress?: string | null }> | null;
}

/**
 * Clerk's user, reduced to the two strings the footer needs.
 *
 * `fullName` is null whenever either half is missing, so the parts are joined
 * by hand as the next-best answer, and `username` after that — any of the
 * three beats printing an id. Same for the address: `primaryEmailAddress` is
 * null on an account that has never marked one, and the first address on file
 * is still the right thing to show.
 */
export function personFromClerkUser(user: ClerkUserLike | null | undefined): AuthPerson | null {
  if (!user) return null;

  const parts = [present(user.firstName), present(user.lastName)].filter(Boolean);
  const name =
    present(user.fullName) ??
    (parts.length > 0 ? parts.join(" ") : undefined) ??
    present(user.username);

  const email =
    present(user.primaryEmailAddress?.emailAddress) ??
    present(user.emailAddresses?.[0]?.emailAddress);

  return { name, email };
}
